import CIMSTable from 'components/CIMSTable'
import { useCIMSDrawer } from 'components/CIMSDrawer'
import RecipientSelectProvider from 'components/CIMSSelect/common/customWorkflows/RecipientSelect/RecipientSelectContext'
import type { RecipientValue } from 'components/CIMSSelect/common/customWorkflows/RecipientSelect/types'
import { useCheckPermission } from 'hooks/useCheckPermission'
import {
  simpleCreateNotification,
  simpleEditNotification,
  simpleRemoveNotification,
  simpleRestoreNotification,
  simpleSuccessNotification,
} from 'hooks/notificationHandler'
import { useRouteMatches } from 'hooks/useRouteMatches'
import type { Key } from 'react'
import { useState } from 'react'
import {
  useBulkEditNotificationRecipientsMutation,
  useCreateNotificationMutation,
  useEditNotificationMutation,
  useRemoveNotificationMutation,
  useRestoreNotificationMutation,
  useNotificationsQuery,
} from 'schema'
import type { NotificationRecipientInput } from 'schema'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import AddRecipientsModal from './components/AddRecipientsModal'
import NotificationDrawer from './components/NotificationDrawer'
import { useColumns } from './hooks/useColumns'
import type { Notification } from './types'
import type { NotificationFormSubmission } from './components/NotificationDrawer/types'

function notificationRecipientInput(
  recipient: Notification['to'][number]
): NotificationRecipientInput {
  if (recipient.__typename === 'User')
    return { type: 'user', userId: recipient.userId }

  return {
    type: 'non_user_email',
    nonUserEmailId: recipient.nonUserEmailId,
  }
}

function deletedByLabel({ deletedBy }: Notification) {
  return deletedBy?.displayName ?? deletedBy?.mail ?? null
}

function Notifications() {
  const { param } = useRouteMatches()
  const workflowId = param('workflowId')
  const canMutate = useCheckPermission('admin')
  const drawer = useCIMSDrawer<Notification>()
  const [addRecipientsVisible, setAddRecipientsVisible] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])
  const selectedNotificationIds = selectedRowKeys.map(String)
  const selectedCount = selectedNotificationIds.length

  const { data, loading, refetch } = useNotificationsQuery({
    ...DefaultQueryHookOptions,
    skip: !workflowId,
    variables: workflowId ? { workflowId } : undefined,
  })

  const [createNotification, { loading: createLoading }] =
    useCreateNotificationMutation({
      ...DefaultMutationHookOptions,
      onCompleted: () => simpleCreateNotification('Notification'),
    })
  const [editNotification, { loading: editLoading }] =
    useEditNotificationMutation({
      ...DefaultMutationHookOptions,
      onCompleted: () => simpleEditNotification('Notification'),
    })
  const [removeNotification] = useRemoveNotificationMutation({
    ...DefaultMutationHookOptions,
    onCompleted: () => simpleRemoveNotification('Notification'),
  })
  const [restoreNotification] = useRestoreNotificationMutation({
    ...DefaultMutationHookOptions,
    onCompleted: () => simpleRestoreNotification('Notification'),
  })
  const [bulkEditRecipients, { loading: bulkEditLoading }] =
    useBulkEditNotificationRecipientsMutation({
      ...DefaultMutationHookOptions,
      onCompleted: () =>
        simpleSuccessNotification('Notification recipients updated'),
    })

  const workflow = data?.listWorkflows.records[0]
  const fields = workflow?.fields.records ?? []
  const statuses = workflow?.statuses.records ?? []
  const templates = data?.listNotificationTemplates.records ?? []

  const duplicate = async (notification: Notification) => {
    await createNotification({
      variables: {
        input: {
          conditions: notification.conditions,
          templateId: notification.templateId,
          to: notification.to.map(notificationRecipientInput),
          triggerSettings: notification.triggerSettings,
        },
      },
    })
    await refetch()
  }
  const remove = async ({ id }: Notification) => {
    await removeNotification({ variables: { input: { id } } })
    await refetch()
  }
  const restore = async ({ id }: Notification) => {
    await restoreNotification({ variables: { input: { id } } })
    await refetch()
  }
  const clearRecipients = async () => {
    const { data } = await bulkEditRecipients({
      variables: {
        input: {
          notificationIds: selectedNotificationIds,
          to: [],
        },
      },
    })
    if (!data) return

    setSelectedRowKeys([])
    await refetch()
  }
  const addRecipients = async (recipients: RecipientValue[]) => {
    const { data } = await bulkEditRecipients({
      variables: {
        input: {
          notificationIds: selectedNotificationIds,
          to: recipients,
        },
      },
    })
    if (!data) return

    setAddRecipientsVisible(false)
    setSelectedRowKeys([])
    await refetch()
  }
  const saveNotification = async (submission: NotificationFormSubmission) => {
    const values = {
      conditions: submission.conditions,
      templateId: submission.templateId,
      to: submission.to,
      triggerSettings: submission.triggerSettings,
    }

    try {
      const result = drawer.state
        ? await editNotification({
            variables: {
              input: {
                ...values,
                id: drawer.state.id,
              },
            },
          })
        : await createNotification({
            variables: {
              input: values,
            },
          })
      if (!result.data) return
    } catch {
      /*
       * Default Apollo mutation handlers already surface failures. Consume
       * rejected promises because the form does not await this callback.
       */
      return
    }

    drawer.close()

    try {
      await refetch()
    } catch {
      // The default Apollo query handler already surfaces refetch failures.
    }
  }

  return (
    <>
      <CIMSTable
        actions={{
          header: {
            create: {
              entity: 'Notification',
              loading,
              modal: { type: 'custom', toggle: () => drawer.open() },
              permission: canMutate,
            },
            custom: [
              {
                confirm: {
                  disabled: !selectedCount,
                  onConfirm: clearRecipients,
                  placement: 'topRight',
                  title: `Clear recipients from ${selectedCount} selected notification${
                    selectedCount === 1 ? '' : 's'
                  }?`,
                },
                disabled: selectedCount
                  ? false
                  : { message: 'Select notifications first' },
                loading: bulkEditLoading,
                onClick: () => {},
                permission: canMutate,
                text: 'Clear',
              },
              {
                disabled: selectedCount
                  ? false
                  : { message: 'Select notifications first' },
                loading: bulkEditLoading,
                onClick: () => setAddRecipientsVisible(true),
                permission: canMutate,
                text: 'Add recipients',
              },
            ],
          },
          row: {
            edit: {
              modal: {
                type: 'custom',
                toggle: (notification) =>
                  drawer.open(notification ?? undefined),
              },
              permission: canMutate,
            },
            remove: {
              onRemove: remove,
              permission: canMutate,
            },
            restore: {
              default: 'active',
              metadata: (notification) => ({
                removed: notification.deleted,
                removedBy: deletedByLabel(notification),
                removedDate: notification.deletedDate,
              }),
              onRestore: restore,
              permission: canMutate,
            },
            custom: [
              {
                loading: createLoading,
                onClick: duplicate,
                permission: canMutate,
                text: 'Duplicate',
              },
            ],
          },
        }}
        columns={useColumns({ fields, statuses })}
        loading={loading}
        records={data?.listNotifications.records ?? []}
        rowKey="id"
        rowSelection={
          canMutate
            ? {
                selectedRowKeys,
                onChange: setSelectedRowKeys,
              }
            : undefined
        }
      />
      {canMutate && (
        <RecipientSelectProvider>
          <NotificationDrawer
            fields={fields}
            loading={createLoading || editLoading}
            notification={drawer.state}
            onClose={drawer.close}
            onSubmit={saveNotification}
            open={drawer.visible}
            statuses={statuses}
            templates={templates}
          />
          <AddRecipientsModal
            loading={bulkEditLoading}
            onAdd={addRecipients}
            onClose={() => setAddRecipientsVisible(false)}
            visible={addRecipientsVisible}
          />
        </RecipientSelectProvider>
      )}
    </>
  )
}

export default Notifications
